import { Button, Divider, Drawer, Flex, Form, Input, Space, Tag, Typography, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useUpdateQuestion } from '../../api/Questions';
import { useUpdateSurvey } from '../../api/Surveys';
import { QuestionProcessingType } from '../../constants';
import { questionType, surveyType } from '../../types';

const { TextArea } = Input;
const { Text } = Typography;

const StyledDivider = styled(Divider)`
  margin: 8px 0;
`;

const SmallTag = styled(Tag)`
  font-size: 10px !important;
  line-height: 1;
  padding: 2px 6px;
  height: auto;
  border-radius: 4px;
  margin-top: 4px;
  display: block; /* ensures tag starts on a new line below the question text */
  width: fit-content;
`;

function EditSurveyDrawer({ survey, questions, open, onClose }) {
  const [form] = Form.useForm();
  const [editedQuestions, setEditedQuestions] = useState({});

  const { mutateAsync: updateSurvey, isLoading: updateSurveyLoading } = useUpdateSurvey();
  const { mutateAsync: updateQuestion, isLoading: updateQuestionLoading } = useUpdateQuestion();

  useEffect(() => {
    if (open) {
      form.setFieldsValue(survey);
    }
  }, [open, form, survey]);

  const closeDrawer = () => {
    setEditedQuestions({});
    onClose();
  };

  const handleSave = async (formValues) => {
    try {
      const surveyPromise = updateSurvey({ surveyId: survey.id, data: formValues });
      const questionPromises = Object.keys(editedQuestions).map((questionId) =>
        updateQuestion({
          questionId,
          surveyId: survey.id,
          data: { name: editedQuestions[questionId] },
        }),
      );
      await Promise.all([surveyPromise, ...questionPromises]);
      message.success('Survey details updated successfully');
    } catch {
      // error is handled in mutation hooks
    } finally {
      closeDrawer();
    }
  };

  return (
    <Drawer
      open={open}
      onClose={closeDrawer}
      closable={false}
      width={570}
      title="Edit survey details"
      footer={
        <Space>
          <Button onClick={closeDrawer}>Cancel</Button>
          <Button
            onClick={() => {
              form
                .validateFields()
                .then((values) => {
                  handleSave(values);
                })
                .catch(() => {});
            }}
            type="primary"
            loading={updateSurveyLoading || updateQuestionLoading}
            disabled={updateSurveyLoading || updateQuestionLoading}
          >
            Save changes
          </Button>
        </Space>
      }
    >
      <Form layout="vertical" form={form}>
        <Form.Item
          label="Project name"
          name="name"
          rules={[{ required: true, message: 'Project name is required.' }]}
        >
          <Input placeholder="How your survey will be labeled" />
        </Form.Item>
        <Form.Item label="Coding guidance" name="survey_goals">
          <TextArea placeholder="Add research questions, goals and desired outcomes" />
        </Form.Item>
      </Form>
      <Flex vertical gap={8}>
        <Text strong>Edit Questions</Text>
        <div data-cy="question-list">
          {questions.map((question) => {
            const processingTag = () => {
              if (!question.has_themes) return null;
              switch (question.processing_type) {
                case QuestionProcessingType.AI_GENERATED:
                  return <SmallTag color="blue">AI generated suggestions</SmallTag>;
                case QuestionProcessingType.REUSE_CODE_FRAME:
                  return <SmallTag color="purple">Imported codeframe</SmallTag>;
                case QuestionProcessingType.SKIP:
                  return <SmallTag color="default">Manually provided codeframe</SmallTag>;
                default:
                  return null;
              }
            };
            return (
              <React.Fragment key={question.id}>
                <Text
                  data-cy="edit-question"
                  editable={{
                    onChange: (value) => {
                      setEditedQuestions({ ...editedQuestions, [question.id]: value });
                    },
                  }}
                >
                  {editedQuestions[question.id] || question.text}
                </Text>
                {processingTag()}
                <StyledDivider />
              </React.Fragment>
            );
          })}
        </div>
        <Text strong>Need something else?</Text>
        <Text>
          To make advanced changes or to delete a survey,{' '}
          <a href="mailto:support@fathomthat.ai">contact us</a>
        </Text>
      </Flex>
    </Drawer>
  );
}

EditSurveyDrawer.propTypes = {
  survey: surveyType.isRequired,
  questions: PropTypes.arrayOf(questionType).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EditSurveyDrawer;
